// frontend/src/widgets/CostCentreTaxonomyWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-10-23
// Updated:      2026-10-23 11:48 BST
//
// Description:
//   Tenant-scoped cost centre taxonomy management for System → Resources.
//   Uses the established /api/cost-centers endpoints through axiosInstance.
//   The backend remains authoritative for role-based write access and tenant isolation.
//
// Change Log (BST):
// 2026-10-23 11:33  Initial category/subtype management; loading, validation,
//                   conflict/error handling, and read-only fallback on HTTP 403.
// 2026-10-23 11:48  Apply Vision action/general button theme classes, readable
//                   foreground/background contrast and clear selection styling.

import React, { useCallback, useEffect, useState } from "react";
import api from "../api/axiosInstance";

const fieldClass = "min-h-[40px] min-w-0 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900";
const buttonClass = "min-h-[40px] rounded px-3 py-2 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50";
const actionButtonClass = `${buttonClass} bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick`;
const generalButtonClass = `${buttonClass} bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick`;
const asList = (value) => Array.isArray(value) ? value : Array.isArray(value?.items) ? value.items : [];
const errorText = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || JSON.stringify(item)).join("; ");
  return error?.message || "The request failed.";
};
const labelFor = (item) => item?.name || item?.code || `#${item?.id}`;
const codeFrom = (value) => value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export default function CostCentreTaxonomyWidget() {
  const [categories, setCategories] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState({ code: "", name: "" });
  const [subtypeDraft, setSubtypeDraft] = useState({ code: "", name: "" });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubtype, setEditingSubtype] = useState(null);

  const selected = categories.find((item) => Number(item.id) === Number(selectedId));

  const loadCategories = useCallback(async (preferredId) => {
    const response = await api.get("/cost-centers/");
    const items = asList(response.data);
    setCategories(items);
    setSelectedId((previous) => {
      const desired = preferredId === undefined ? previous : preferredId;
      return items.some((item) => Number(item.id) === Number(desired))
        ? Number(desired) : items.length ? Number(items[0].id) : null;
    });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const response = await api.get("/cost-centers/");
        if (!alive) return;
        const items = asList(response.data);
        setCategories(items);
        setSelectedId(items.length ? Number(items[0].id) : null);
        setError("");
      } catch (err) {
        if (alive) setError(`Unable to load cost centres: ${errorText(err)}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setEditingSubtype(null);
    if (selectedId == null) {
      setSubtypes([]);
      return undefined;
    }
    (async () => {
      try {
        const response = await api.get(`/cost-centers/${selectedId}/subtypes`);
        if (alive) {
          setSubtypes(asList(response.data));
          setError("");
        }
      } catch (err) {
        if (alive) {
          setSubtypes([]);
          setError(`Unable to load subtypes: ${errorText(err)}`);
        }
      }
    })();
    return () => { alive = false; };
  }, [selectedId]);

  const reload = async (preferredId = selectedId) => {
    await loadCategories(preferredId);
    if (preferredId != null) {
      const response = await api.get(`/cost-centers/${preferredId}/subtypes`);
      setSubtypes(asList(response.data));
    } else {
      setSubtypes([]);
    }
  };

  const perform = async (action, success, preferredId = selectedId) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await action();
      await reload(preferredId);
      setMessage(success);
      return true;
    } catch (err) {
      if (err?.response?.status === 403) setReadOnly(true);
      setError(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    const name = categoryDraft.name.trim();
    const code = codeFrom(categoryDraft.code || name);
    if (!name || !code) { setError("Category name and code are required."); return; }
    const payload = { code, name };
    const ok = await perform(
      () => editingCategory
        ? api.put(`/cost-centers/${editingCategory.id}`, payload)
        : api.post("/cost-centers/", payload),
      editingCategory ? "Category updated." : "Category created."
    );
    if (ok) { setEditingCategory(null); setCategoryDraft({ code: "", name: "" }); }
  };

  const saveSubtype = async (event) => {
    event.preventDefault();
    if (!selected) return;
    const name = subtypeDraft.name.trim();
    const code = codeFrom(subtypeDraft.code || name);
    if (!name || !code) { setError("Subtype name and code are required."); return; }
    const payload = { code, name };
    const ok = await perform(
      () => editingSubtype
        ? api.put(`/cost-centers/${selected.id}/subtypes/${editingSubtype.id}`, payload)
        : api.post(`/cost-centers/${selected.id}/subtypes`, payload),
      editingSubtype ? "Subtype updated." : "Subtype created."
    );
    if (ok) { setEditingSubtype(null); setSubtypeDraft({ code: "", name: "" }); }
  };

  const removeCategory = async (item) => {
    if (!window.confirm(`Delete category "${labelFor(item)}"? Assigned resources and dependent subtypes may prevent deletion.`)) return;
    await perform(() => api.delete(`/cost-centers/${item.id}`), "Category deleted.", null);
  };

  const removeSubtype = async (item) => {
    if (!selected || !window.confirm(`Delete subtype "${labelFor(item)}"? Assigned resources may prevent deletion.`)) return;
    await perform(() => api.delete(`/cost-centers/${selected.id}/subtypes/${item.id}`), "Subtype deleted.");
  };

  const toggleActive = async (item, kind) => {
    if (typeof item.active !== "boolean") return;
    const next = !item.active;
    const path = kind === "category"
      ? `/cost-centers/${item.id}`
      : `/cost-centers/${selected.id}/subtypes/${item.id}`;
    await perform(() => api.put(path, { active: next }), `${kind === "category" ? "Category" : "Subtype"} ${next ? "activated" : "deactivated"}.`);
  };

  return (
    <section className="min-w-0 text-gray-900" aria-label="Cost centre taxonomy management">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-base font-semibold text-gray-900">Cost Centre Taxonomy</h4>
          <p className="text-xs text-gray-600">Categories and subtypes are scoped to the current tenant. Existing assignments are protected by the backend.</p>
        </div>
        <button type="button" className={generalButtonClass} disabled={busy || loading}
          onClick={() => { setError(""); setMessage(""); reload().catch((err) => setError(errorText(err))); }}>
          Refresh
        </button>
      </div>
      {loading && <p className="text-sm text-gray-600">Loading taxonomy…</p>}
      {readOnly && <p className="mb-2 text-sm text-amber-800">Write access denied by the server. This session is read-only.</p>}
      {error && <p role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      {message && <p role="status" className="mb-3 rounded border border-green-200 bg-green-50 p-2 text-sm text-green-800">{message}</p>}
      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="min-w-0 rounded border border-gray-200 bg-white p-3 text-gray-900">
          <h5 className="mb-2 font-semibold">Categories</h5>
          <div className="space-y-2">
            {categories.map((item) => (
              <div key={item.id} className={`flex flex-wrap items-center gap-2 rounded border p-2 ${Number(selectedId) === Number(item.id) ? "border-gray-500 bg-gray-100" : "border-gray-200 bg-white"}`}>
                <button type="button" className="min-w-0 flex-1 rounded bg-white px-2 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700" onClick={() => setSelectedId(Number(item.id))}>
                  <span className="font-semibold">{labelFor(item)}</span> <span className="text-xs text-gray-500">({item.code})</span>
                  {item.active === false && <span className="ml-2 text-xs text-red-700">Inactive</span>}
                </button>
                {!readOnly && <>
                  <button type="button" className={generalButtonClass} disabled={busy} onClick={() => { setEditingCategory(item); setCategoryDraft({ code: item.code || "", name: item.name || "" }); }}>Edit</button>
                  {typeof item.active === "boolean" && <button type="button" className={generalButtonClass} disabled={busy} onClick={() => toggleActive(item, "category")}>{item.active ? "Deactivate" : "Activate"}</button>}
                  <button type="button" className={generalButtonClass} disabled={busy} onClick={() => removeCategory(item)}>Delete</button>
                </>}
              </div>
            ))}
            {!loading && categories.length === 0 && <p className="text-sm text-gray-500">No categories available.</p>}
          </div>
          {!readOnly && <form className="mt-4 space-y-2" onSubmit={saveCategory}>
            <h6 className="text-sm font-semibold">{editingCategory ? "Edit category" : "Add category"}</h6>
            <input className={fieldClass} aria-label="Category name" placeholder="Name" value={categoryDraft.name} onChange={(e) => setCategoryDraft((old) => ({ ...old, name: e.target.value }))} required />
            <input className={fieldClass} aria-label="Category code" placeholder="Code (automatically generated if blank)" value={categoryDraft.code} onChange={(e) => setCategoryDraft((old) => ({ ...old, code: e.target.value }))} />
            <div className="flex flex-wrap gap-2">
              <button type="submit" disabled={busy || loading} className={actionButtonClass}>{editingCategory ? "Save category" : "Add category"}</button>
              {editingCategory && <button type="button" className={generalButtonClass} onClick={() => { setEditingCategory(null); setCategoryDraft({ code: "", name: "" }); }}>Cancel</button>}
            </div>
          </form>}
        </div>
        <div className="min-w-0 rounded border border-gray-200 bg-white p-3 text-gray-900">
          <h5 className="mb-2 font-semibold">Subtypes {selected ? `— ${labelFor(selected)}` : ""}</h5>
          {!selected && <p className="text-sm text-gray-500">Select a category to manage its subtypes.</p>}
          {selected && <>
            <div className="space-y-2">
              {subtypes.map((item) => (
                <div key={item.id} className="flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-white p-2 text-gray-900">
                  <span className="min-w-0 flex-1 text-sm text-gray-900"><strong>{labelFor(item)}</strong> <span className="text-xs text-gray-500">({item.code})</span>{item.active === false && <span className="ml-2 text-xs text-red-700">Inactive</span>}</span>
                  {!readOnly && <>
                    <button type="button" className={generalButtonClass} disabled={busy} onClick={() => { setEditingSubtype(item); setSubtypeDraft({ code: item.code || "", name: item.name || "" }); }}>Edit</button>
                    {typeof item.active === "boolean" && <button type="button" className={generalButtonClass} disabled={busy} onClick={() => toggleActive(item, "subtype")}>{item.active ? "Deactivate" : "Activate"}</button>}
                    <button type="button" className={generalButtonClass} disabled={busy} onClick={() => removeSubtype(item)}>Delete</button>
                  </>}
                </div>
              ))}
              {subtypes.length === 0 && <p className="text-sm text-gray-500">No subtypes available.</p>}
            </div>
            {!readOnly && <form className="mt-4 space-y-2" onSubmit={saveSubtype}>
              <h6 className="text-sm font-semibold">{editingSubtype ? "Edit subtype" : "Add subtype"}</h6>
              <input className={fieldClass} aria-label="Subtype name" placeholder="Name" value={subtypeDraft.name} onChange={(e) => setSubtypeDraft((old) => ({ ...old, name: e.target.value }))} required />
              <input className={fieldClass} aria-label="Subtype code" placeholder="Code (automatically generated if blank)" value={subtypeDraft.code} onChange={(e) => setSubtypeDraft((old) => ({ ...old, code: e.target.value }))} />
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={busy} className={actionButtonClass}>{editingSubtype ? "Save subtype" : "Add subtype"}</button>
                {editingSubtype && <button type="button" className={generalButtonClass} onClick={() => { setEditingSubtype(null); setSubtypeDraft({ code: "", name: "" }); }}>Cancel</button>}
              </div>
            </form>}
          </>}
        </div>
      </div>
    </section>
  );
}
